import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { includes, toString } from 'es-toolkit/compat';
import React from 'react';
import { Link } from 'react-router-dom';

import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { JobStatus, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { useStudyUploadList } from 'utils/queryHooks/useStudyUploadList';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { getLinkToJobsPage } from 'utils/useNavigationToJobsPage';
import { usePermissions } from 'utils/usePermissions';
import { getIngestingUploadCount } from './StudyUploadsStatus';

const StudyActiveIngestStatus = () => {
  const { labId } = useCurrentLabId();
  const { queryParams } = useEncodedFilters();
  const { hasPermission } = usePermissions();
  const selectedStudyId = toString(queryParams.filters?.studyId);
  const hasSelectedStudy =
    Boolean(queryParams.filters?.studyId) && !includes(NON_SPECIFIC_STUDY_VALUES, selectedStudyId);

  const { data: fileList, isError } = useStudyUploadList({ studyId: selectedStudyId, enabled: hasSelectedStudy });
  const ingestingUploadCount = getIngestingUploadCount(fileList);

  if (!hasSelectedStudy || ingestingUploadCount === 0) {
    return null;
  }

  const ingestingFilesText = `${ingestingUploadCount} uploaded ${
    ingestingUploadCount === 1 ? 'file is' : 'files are'
  } ingesting`;
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const activeIngestJobsLocation = getLinkToJobsPage({
    labId,
    jobsFilters: {
      studyId: selectedStudyId,
      types: [JobType.Ingest, JobType.BatchIngest],
      statuses: [JobStatus.Pending, JobStatus.Running],
    },
  });
  const chipProps = {
    label: `${ingestingUploadCount} ingesting`,
    variant: 'outlined' as const,
    icon: <CircularProgress size={14} color="inherit" aria-hidden />,
    sx: { borderRadius: '100px', fontSize: '14px', fontWeight: 'bold', height: '40px', px: 0.5 },
  };

  return (
    <Tooltip
      title={
        isError
          ? `${ingestingFilesText}. Ingest status may be out of date.`
          : canViewJobs
          ? `${ingestingFilesText}. View active ingest jobs.`
          : ingestingFilesText
      }
      placement="bottom"
      arrow
    >
      {canViewJobs ? (
        <Chip
          {...chipProps}
          clickable
          component={Link}
          to={activeIngestJobsLocation}
          aria-label={`${ingestingFilesText}. View active ingest jobs`}
        />
      ) : (
        <Chip {...chipProps} aria-label={ingestingFilesText} />
      )}
    </Tooltip>
  );
};

export default StudyActiveIngestStatus;
